import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import Loading from '@components/Loading';
import axios from '@helper/axiosInterceptor';
import { getApiPayload } from '@helper/apiError';
import { pageConstants } from '@helper/constants';
import { clearQrCart, getQrBrowserSessions, getQrCart, getQrSession, isQrVisit, qrPath, setQrBrowserSession, setQrCart, storedQrToken } from '@helper/qrNavigation';
import { qrPublicEntryTarget, replaceQrEntryLocation } from '@helper/qrPublicEntry';

const LegacyQrRedirect = ({ page = 'home', paramName = '' }) => {
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const publicEntryTarget = params.tokenqr && page === 'home' ? qrPublicEntryTarget(location.pathname, location.search) : null;
    const tokenqr = params.tokenqr || storedQrToken() || localStorage.getItem('tokenqr') || '';
    const identifier = paramName ? params?.[paramName] : '';
    const [exchanged, setExchanged] = useState(null);
    const [failure, setFailure] = useState('');
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (publicEntryTarget) replaceQrEntryLocation(publicEntryTarget);
    }, [publicEntryTarget]);

    useEffect(() => {
        if (publicEntryTarget || !tokenqr || isQrVisit(tokenqr)) return;
        let active = true;
        const controller = new AbortController();
        setFailure('');
        const previous = getQrSession(tokenqr);
        const previousCart = getQrCart(tokenqr);
        const request = { action: 'exchange', tokenqr };
        if (previous?.token) request.token = previous.token;
        axios.post('browser_session.php', request, {
            headers: { 'X-QR-Request': '1' }, withCredentials: true, timeout: 15000, signal: controller.signal,
        }).then(response => {
            if (!active) return;
            const result = getApiPayload(response);
            // The server confirms ownership before a legacy cart can adopt the
            // new public marker. A fresh visit never inherits an old cart.
            setQrBrowserSession(result.visit, result);
            clearQrCart(tokenqr);
            localStorage.removeItem(`orderpro_qr_session:${tokenqr}`);
            if (previous?.target_kind === 'shop') {
                localStorage.removeItem(`orderpro_qr_shop_session:${Number(previous.app_no)}:${Number(previous.s_no)}`);
            }
            const oldSessions = getQrBrowserSessions();
            Object.keys(oldSessions).forEach(key => { if (oldSessions[key] === previous?.token) delete oldSessions[key]; });
            localStorage.setItem('orderpro_qr_browser_sessions', JSON.stringify(oldSessions));
            if (result.resumed === true && previousCart?.token === previous?.token) {
                setQrCart(result.visit, { ...previousCart, token: result.token });
            }
            if (localStorage.getItem('tokenqr') === tokenqr) localStorage.removeItem('tokenqr');
            localStorage.removeItem('tokenqr_local');
            localStorage.removeItem('token_local');
            setExchanged(result.visit);
        }).catch(error => {
            if (!active) return;
            const result = getApiPayload(error?.response?.data);
            if (result.msg === 'SERVICE_INACTIVE') setFailure('service_inactive');
            else if (Number(error?.response?.status) === 403) setExchanged('');
            else setFailure('retry');
        });
        return () => { active = false; controller.abort(); };
    }, [publicEntryTarget, tokenqr, retryCount]);

    if (publicEntryTarget) return <Loading />;
    if (failure === 'service_inactive') return <Navigate to={`${pageConstants.subFolder}/service_inactive`} replace />;
    if (failure === 'retry') return <div className="nl-wrapper">
        <div className="qr-inline-error" role="alert">
            <p>{t('tr_validate.popup_alert_connect_error', 'Unable to connect. Please try again.')}</p>
            <button type="button" onClick={() => setRetryCount(count => count + 1)}>{t('tr_action.retry', 'Retry')}</button>
        </div>
    </div>;
    if (tokenqr && !isQrVisit(tokenqr) && exchanged === null) return <Loading />;
    const visit = isQrVisit(tokenqr) ? tokenqr : exchanged || '';
    const target = qrPath(visit, page, identifier);

    return <Navigate replace to={`${target}${location.search || ''}`} state={isQrVisit(visit) ? { orderproQrVisit: visit } : null} />;
};

export default LegacyQrRedirect;
